import axios, { AxiosRequestConfig } from "axios";
import { useMemo } from "react";
import { KeycloakRPTSingleton, KeycloakSingleton } from "../components/keycloak-provider";
import { BACKOFFICE_MANAGER_URL } from "../modules/witadmin/request";
import { getPropertyFromRptToken, isJWTTokenExpired } from "./auth";
import { useConfig } from "./config";

export interface IHttpClient {
  get<T>(path: string, config?: AxiosRequestConfig): Promise<T>;

  post<Req, Res>(path: string, body: Req, config?: AxiosRequestConfig): Promise<Res>;

  patch<Req, Res>(path: string, body: Req, config?: AxiosRequestConfig): Promise<Res>;

  put<Req, Res>(path: string, body: Req, config?: AxiosRequestConfig): Promise<Res>;

  delete<Res>(path: string, config?: AxiosRequestConfig): Promise<Res>;
}

interface HttpClientOptions {
  usePropertyHeader?: boolean;
  useNonRptToken?: boolean;
}

export class HttpClient implements IHttpClient {
  constructor(private baseUrl: string, private readonly options?: HttpClientOptions) {
    if (options?.usePropertyHeader && options?.useNonRptToken) {
      throw new Error("You can't config usePropertyHeader and useNonRptToken at the same time.");
    }
  }

  async get<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const fullUrl = `${this.baseUrl}${path}`;
    const response = await axios.get<T>(fullUrl, this.addAuthzHeaders(config));
    return response.data;
  }

  async post<Req, Res>(path: string, body: Req, config?: AxiosRequestConfig): Promise<Res> {
    const fullUrl = `${this.baseUrl}${path}`;
    const response = await axios.post<Res>(fullUrl, body, this.addAuthzHeaders(config));
    return response.data;
  }

  async patch<Req, Res>(path: string, body: Req, config?: AxiosRequestConfig): Promise<Res> {
    const fullUrl = `${this.baseUrl}${path}`;
    const response = await axios.patch<Res>(fullUrl, body, this.addAuthzHeaders(config));
    return response.data;
  }

  async put<Req, Res>(path: string, body: Req, config?: AxiosRequestConfig): Promise<Res> {
    const fullUrl = `${this.baseUrl}${path}`;
    const response = await axios.put<Res>(fullUrl, body, this.addAuthzHeaders(config));
    return response.data;
  }

  async delete<Res>(path: string, config?: AxiosRequestConfig): Promise<Res> {
    const fullUrl = `${this.baseUrl}${path}`;
    const response = await axios.delete<Res>(fullUrl, this.addAuthzHeaders(config));
    return response.data;
  }

  private addAuthzHeaders(config?: AxiosRequestConfig): AxiosRequestConfig {
    const token = this.options?.useNonRptToken
      ? KeycloakSingleton?.token
      : KeycloakRPTSingleton.rpt;

    if (token && isJWTTokenExpired(token)) {
      window.location.reload();
    }

    const accessToken = token as string;
    let headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
    };

    if (this.options && this.options.usePropertyHeader) {
      const propertyTicker = getPropertyFromRptToken(accessToken);
      if (!propertyTicker) {
        throw new Error("No property ticker found in RPT token.");
      }
      headers = { ...headers, "x-property": propertyTicker };
    }

    return {
      ...(config ?? {}),
      headers: {
        ...headers,
        ...(config?.headers || {}),
      },
    };
  }
}

export function useHttpClient(baseUrl: string, options?: HttpClientOptions): IHttpClient {
  return useMemo(() => new HttpClient(baseUrl, options), [baseUrl, options]);
}

export function useApiClient(options?: { usePropertyHeader: boolean }): IHttpClient {
  const config = useConfig();
  return useMemo(() => {
    const clientOptions = { usePropertyHeader: true, ...options };
    return new HttpClient(config.apiEndpoint, clientOptions);
  }, [config.apiEndpoint, options]);
}

export function usePaymentApiClient(options?: { usePropertyHeader: boolean }): IHttpClient {
  const config = useConfig();
  return useMemo(() => {
    const clientOptions = { ...options };
    return new HttpClient(config.paymentsApiEndpoint, clientOptions);
  }, [config.paymentsApiEndpoint, options]);
}

export function useBackofficeClient(): IHttpClient {
  return useMemo(() => new HttpClient(BACKOFFICE_MANAGER_URL, { usePropertyHeader: true }), []);
}
